import { Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HomeService, HomeSearchGroups } from './home.service';

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="palette" *ngIf="open" (click)="close()">
      <div class="palette-card" role="dialog" aria-label="Search" (click)="$event.stopPropagation()">
        <input [(ngModel)]="query" (ngModelChange)="onType()" placeholder="Search contacts, notes, profiles, pages" aria-label="Search" autofocus />
        <p *ngIf="error">Couldn't load. <button type="button" (click)="run()">Retry</button></p>
        <ng-container *ngFor="let group of groups">
          <h2 *ngIf="group.items.length">{{ group.label }}</h2>
          <button type="button" *ngFor="let item of group.items" (click)="go(item.href)">
            <strong>{{ item.title }}</strong>
            <span>{{ item.subtitle }}</span>
          </button>
        </ng-container>
        <p *ngIf="query.trim().length >= 2 && !loading && empty" class="quiet">No matches.</p>
      </div>
    </div>
  `,
  styles: [`
    .palette { position: fixed; inset: 0; z-index: 80; background: color-mix(in srgb, var(--text) 40%, transparent); display: flex; justify-content: center; padding-top: 12vh; }
    .palette-card { width: min(560px, calc(100% - 32px)); max-height: 70vh; overflow: auto; background: var(--surface); color: var(--text); border-radius: var(--r-card, 16px); padding: 12px; box-shadow: var(--shadow, 0 12px 40px rgb(0 0 0 / 18%)); }
    input { width: 100%; box-sizing: border-box; border: 1px solid var(--border); background: var(--surface); color: var(--text); border-radius: 10px; padding: 12px; font-size: 16px; }
    h2 { margin: 12px 0 4px; font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-3); }
    button { display: flex; width: 100%; text-align: left; gap: 8px; justify-content: space-between; border: 0; background: transparent; color: inherit; padding: 8px; border-radius: 8px; cursor: pointer; }
    button:hover { background: var(--accent-soft); }
    span, .quiet { color: var(--text-3); }
  `],
})
export class CommandPaletteComponent {
  open = false;
  query = '';
  loading = false;
  error = '';
  result: HomeSearchGroups = { contacts: [], notes: [], profiles: [], pages: [] };
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private home: HomeService, private router: Router) {}

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.open = !this.open;
      if (!this.open) this.query = '';
    } else if (event.key === 'Escape' && this.open) {
      this.close();
    }
  }

  get groups(): { label: string; items: { title: string; subtitle: string; href: string }[] }[] {
    return [
      { label: 'Contacts', items: this.result.contacts },
      { label: 'Notes', items: this.result.notes },
      { label: 'Profiles', items: this.result.profiles },
      { label: 'Pages', items: this.result.pages },
    ];
  }

  get empty(): boolean {
    return this.groups.every((group) => group.items.length === 0);
  }

  onType(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), 250);
  }

  run(): void {
    const q = this.query.trim();
    this.error = '';
    if (q.length < 2) {
      this.result = { contacts: [], notes: [], profiles: [], pages: [] };
      return;
    }
    this.loading = true;
    this.home.search(q).subscribe({
      next: (result) => {
        this.result = result;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = "Couldn't load.";
      },
    });
  }

  go(href: string): void {
    this.close();
    this.router.navigateByUrl(href);
  }

  close(): void {
    this.open = false;
    this.query = '';
  }
}
